import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Row,
  Col,
  Avatar,
  Descriptions,
  Timeline,
  Image,
  Space,
  Typography,
  Divider,
  Collapse,
  PageHeader,
  PageStack,
} from "lib/ui";
import {
  PlusOutlined,
  MessageOutlined,
  DeleteOutlined,
  EditOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  VerifiedOutlined,
  WarningOutlined,
  DollarOutlined,
  CreditCardOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  CarryOutOutlined,
} from "lib/icons";
import ApiPath from "../../../hooks/ApiFetch";

import { useParams } from "react-router-dom";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const CustomerDetailsPage = () => {
  const { id } = useParams();
  const [customerData, setCustomerData] = useState(null);
  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const customerId = id; // Assuming you get the customer ID from the URL params
  // -----------------------------------------------------------------------------------------

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const customerResponse = await ApiPath.get(
          `/users/oneuser/${customerId}`
        );

        const customer = customerResponse.data;
        console.log("customerResponse", customerResponse.data);
        setCustomerData(customer);

        const ordersResponse = await ApiPath.get(
          `/orders/oneUserOrder/${customerId}`
        ); // Adjusted to use customerId

        const orders = ordersResponse.data;
        console.log("ordersResponse", ordersResponse.data); // Debug the orders response
        setOrdersData(orders);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [customerId]);

  const formatDate = (dateString) => {
    if (!dateString) return "Not yet";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTimeRange = (timeString) => {
    if (!timeString) return "Not scheduled";
    const matches = timeString.match(
      /(\d{1,2}:\d{2} [AP]M) - (\d{1,2}:\d{2} [AP]M)/
    );
    return matches && matches.length >= 3
      ? `${matches[1]} to ${matches[2]}`
      : timeString;
  };

  const itemsColumns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (text, record) => (
        <Space>
          <Image
            src={record.image}
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: 4 }}
            fallback="https://via.placeholder.com/50"
          />
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary">{record.categoryName}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price) => `AED ${price?.toFixed(2) || "0.00"}`,
    },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    {
      title: "Total",
      key: "total",
      render: (_, record) =>
        `AED ${(record.price * record.quantity)?.toFixed(2) || "0.00"}`,
    },
  ];

  const renderOrderDetails = (order) => {
    const orderItems = Array.isArray(order.items)
      ? order.items.map((item) => ({
          key: item._id,
          ...item,
          total: item.price * item.quantity,
        }))
      : [];

    return (
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card
            title="Order Timeline"
            bordered={false}
            style={{ boxShadow: "none" }}
          >
            <Timeline mode="left">
              {Array.isArray(order.stage) && order.stage.length > 0 ? (
                order.stage.map((stage) => (
                  <Timeline.Item
                    key={stage._id}
                    color={stage.completed ? "green" : "gray"}
                    dot={
                      stage.completed ? (
                        <CheckCircleOutlined />
                      ) : (
                        <ClockCircleOutlined />
                      )
                    }
                  >
                    <Text strong>{stage.title}</Text>
                    <br />
                    <Text type="secondary">{formatDate(stage.date)}</Text>
                  </Timeline.Item>
                ))
              ) : (
                <Text>No timeline available</Text>
              )}
            </Timeline>
          </Card>
          <Divider style={{ margin: "16px 0" }} />
          <Card
            title="Delivery Information"
            bordered={false}
            style={{ boxShadow: "none" }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Delivery Address">
                <EnvironmentOutlined style={{ marginRight: 8 }} />
                {order.deliveryAddress?.address || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery Time">
                <CalendarOutlined style={{ marginRight: 8 }} />
                {formatTimeRange(order.deliveryDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Pickup Time">
                <CarryOutOutlined style={{ marginRight: 8 }} />
                {formatTimeRange(order.pickTime)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title="Order Summary"
            bordered={false}
            style={{ boxShadow: "none" }}
          >
            <Table
              columns={itemsColumns}
              dataSource={orderItems}
              pagination={false}
              bordered={false}
              size="middle"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Subtotal</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Text strong>
                        AED {order.subtotal?.toFixed(2) || "0.00"}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text>Delivery Fee</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Text>AED {order.deliveryFee?.toFixed(2) || "0.00"}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text>Discount</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Text>-AED {order.discount?.toFixed(2) || "0.00"}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Text strong>
                        AED {order.totalPrice?.toFixed(2) || "0.00"}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
            <Divider style={{ margin: "16px 0" }} />
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Payment Method">
                <Space>
                  {order.paymentmode === "cash" ? (
                    <DollarOutlined style={{ color: "#52c41a" }} />
                  ) : (
                    <CreditCardOutlined style={{ color: "#1890ff" }} />
                  )}
                  <Text strong>
                    {order.paymentmode?.toUpperCase() || "N/A"}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Amount Due">
                <Text strong>AED {order.balance?.toFixed(2) || "0.00"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Order Date">
                {formatDate(order.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {formatDate(order.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={24}>
          <Space size="middle">
            <Button type="primary" icon={<EditOutlined />} size="large">
              Edit Order
            </Button>
            <Button icon={<DeleteOutlined />} danger size="large">
              Cancel Order
            </Button>
            {order.status === "active" && (
              <Button icon={<CheckCircleOutlined />} size="large">
                Mark as Completed
              </Button>
            )}
            <Button icon={<ShoppingCartOutlined />} size="large">
              Create Similar Order
            </Button>
          </Space>
        </Col>
      </Row>
    );
  };

  const renderAppPreview = (order) => (
    <Card
      title="App Preview"
      bordered={false}
      style={{
        maxWidth: 300,
        margin: "0 auto",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <div style={{ padding: 16, textAlign: "center" }}>
        <Title level={5}>Order #{order.ref}</Title>
        <Text type="secondary">{order.status?.toUpperCase() || "N/A"}</Text>
        <Divider />
        {Array.isArray(order.items) && order.items.length > 0 ? (
          order.items.map((item) => (
            <div key={item._id} style={{ marginBottom: 16 }}>
              <Image
                src={item.image}
                width={100}
                height={100}
                style={{ borderRadius: 4 }}
                fallback="https://via.placeholder.com/100"
              />
              <Text strong block style={{ marginTop: 8 }}>
                {item.product}
              </Text>
              <Text type="secondary">{item.categoryName}</Text>
              <Text block>
                {item.quantity} x AED {item.price?.toFixed(2) || "0.00"}
              </Text>
            </div>
          ))
        ) : (
          <Text>No items available</Text>
        )}
        <Divider />
        <Text strong>Total: AED {order.totalPrice?.toFixed(2) || "0.00"}</Text>
        <br />
        <Text>Delivery: {formatTimeRange(order.deliveryDate)}</Text>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-16 text-slate-500">
        Loading…
      </div>
    );
  }

  // if (error) {
  //   return (
  //     <div style={{ textAlign: "center", padding: 50, color: "red" }}>
  //       Error: {error}
  //     </div>
  //   );
  // }

  if (!customerData) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-16 text-slate-500">
        No customer data found
      </div>
    );
  }

  return (
    <PageStack>
      <PageHeader
        title="Customer details"
        description="Profile, verification status, addresses, and orders for this user."
        actions={
          <Button type="primary" icon={<PlusOutlined />} size="large">
            Create new order
          </Button>
        }
      />

      <Card
        className="mb-6 overflow-hidden rounded-2xl border border-slate-200/80 shadow-card ring-1 ring-slate-900/[0.04]"
      >
        <Row gutter={[32, 16]}>
          <Col xs={24} sm={8} md={6}>
            <div style={{ textAlign: "center" }}>
              <Avatar
                size={120}
                icon={<UserOutlined />}
                style={{ backgroundColor: "#1890ff" }}
              />
              <Title level={4} style={{ marginTop: 16 }}>
                {customerData.name}
              </Title>
              <Space>
                <Text type="secondary">
                  {customerData.firstname} {customerData.lastname}
                </Text>
                <Tag
                  icon={
                    customerData.mobileVerified ? (
                      <VerifiedOutlined />
                    ) : (
                      <WarningOutlined />
                    )
                  }
                  color={customerData.mobileVerified ? "success" : "warning"}
                >
                  {customerData.mobileVerified ? "Verified" : "Unverified"}
                </Tag>
              </Space>
              <Button
                icon={<MessageOutlined />}
                style={{ marginTop: 16, width: "100%" }}
                type="default"
              >
                Send Message
              </Button>
            </div>
          </Col>
          <Col xs={24} sm={16} md={18}>
            <Descriptions
              bordered
              column={{ xs: 1, sm: 2 }}
              size="middle"
              labelStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item label="Email" span={2}>
                <MailOutlined style={{ marginRight: 8 }} />
                {customerData.email}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile">
                <PhoneOutlined style={{ marginRight: 8 }} />
                {customerData.mobileNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Account Status">
                <Tag color={customerData.stage === 3 ? "green" : "orange"}>
                  {customerData.stage === 3 ? "Active" : "Incomplete"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Language">
                {customerData.language.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="User Balance">
                <Text strong>
                  AED {customerData.userBalance?.toFixed(2) || "0.00"}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Member Since">
                {formatDate(customerData.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Primary Address" span={2}>
                <EnvironmentOutlined style={{ marginRight: 8 }} />
                <div>
                  <Text strong>{customerData.address[0].name}</Text>
                  <br />
                  <Text>{customerData.address[0].address}</Text>
                  <br />
                  <Text>
                    Apartment: {customerData.address[0].apartmentNumber}
                  </Text>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      <Card
        title={<Title level={3}>Orders</Title>}
        className="overflow-hidden rounded-2xl border border-slate-200/80 shadow-card ring-1 ring-slate-900/[0.04]"
      >
        {ordersData.length > 0 ? (
          <Collapse accordion>
            {ordersData.map((order) => (
              <Panel
                header={
                  <Space>
                    <Text strong>Order #{order.ref}</Text>
                    <Tag color={order.status === "active" ? "green" : "blue"}>
                      {order.status.toUpperCase()}
                    </Tag>
                    <Text type="secondary">{formatDate(order.createdAt)}</Text>
                  </Space>
                }
                key={order.ref}
              >
                {renderOrderDetails(order)}
              </Panel>
            ))}
          </Collapse>
        ) : (
          <Text>No orders found for this customer.</Text>
        )}
      </Card>
    </PageStack>
  );
};

export default CustomerDetailsPage;
